/* treatmentGroupMap.ts */

/**
 * Maps individual treatment line names to a shared group name.
 * Multiple source names can map to the same group name.
 * Non-mapped treatment line names are displayed as-is.
 *
 * By default all mappable treatment line names are grouped.
 * Each group can be expanded/collapsed interactively in the Sankey diagram.
 *
 * Scope: this map is shared across all conditions (e.g. MM/c90, CLL/c91).
 * Condition-specific regimens (FCR, R-Benda for CLL; Dara-RVd for MM) coexist here
 * and are simply unused for conditions where they do not appear.
 *
 * Overlapping regimen policy:
 *  - Anti-CD38 mAb (Dara/Isa) combinations take priority over PI- or IMiD-only groups.
 *  - Elotuzumab (anti-SLAMF7) combinations are grouped separately from pure IMiD regimens.
 *  - Rd and RD are the same backbone (Lenalidomide + Dexamethasone) at different dose
 *    intensities and are therefore mapped to the same group.
 *  - Mixed PI+IMiD doublets (KR, RVD) are placed in the PI-based group (PI takes priority).
 *
 * Combined regimen policy:
 *  - The backend combines multiple episodes within the same treatment line into a single
 *    node, abbreviating component names and joining them with " + " (e.g. "Dara-RVd + Mel").
 *  - `applyTreatmentGroupMapping` splits such combined names on " + " and looks up each
 *    component individually, then selects the group with the highest clinical priority.
 *  - The "Abbreviation aliases" section below maps abbreviated backend names to groups,
 *    enabling correct grouping for combined nodes.
 */
export const TREATMENT_GROUP_MAP: Record<string, string> = {

 


  // ════════════════════════════════════════════════════════════════════════════
  // MM / C90 — Multiple myeloma
  // ════════════════════════════════════════════════════════════════════════════

  // ── Daratumumab-based (anti-CD38 mAb, takes priority over PI/IMiD) ────────
  // Overlaps with PI-based (Dara-Vd, Dara-Kd, Dara-CyBorD, Dara-RVd)
  // and IMiD-based (Dara-Rd, Dara-Pd) — mAb group takes precedence.
  // Dara-VCD is the internal pipeline key for the OMOP concept "Dara-CyBorD"
  // (VCD = Bortezomib/Velcade + Cyclophosphamide + Dexamethasone = CyBorD).
  // The backend abbreviates it to "Dara-CyBorD"; the full key is listed here
  // as a fallback in case the abbreviation is not applied.
  "Daratumumab monotherapy": "Dara-based",
  "Dara-CyBorD": "Dara-based",
  "Dara-Kd": "Dara-based",
  "Dara-Pd": "Dara-based",
  "Dara-RVd": "Dara-based",
  "Dara-Rd": "Dara-based",
  "Dara-VCD": "Dara-based",   // internal pipeline key for Dara-CyBorD (fallback)
  "Dara-Vd": "Dara-based",
  "Dara-VTD": "Dara-based",

  // ── Isatuximab-based (anti-CD38 mAb) ─────────────────────────────────────
  // Overlaps with Carfilzomib-based (Isa-Kd) and Pomalidomide-based (Isa-Pd).
  "Isatuximab": "Isa-based",
  "Isa-Kd": "Isa-based",
  "Isa-Pd": "Isa-based",

  // ── Elotuzumab-based (anti-SLAMF7 mAb) ───────────────────────────────────
  // Overlaps with Lenalidomide-based (Elo-Rd) and Pomalidomide-based (Elo-Pd).
  "Elotuzumab monotherapy": "Elo-based",
  "Elo-Pd": "Elo-based",
  "Elo-Rd": "Elo-based",

  // ── Bortezomib-based (PI) ─────────────────────────────────────────────────
  // Dara-Vd is intentionally excluded here (see Dara-based above).
  // RVD triplet (Bortezomib + Lenalidomide + Dexamethasone) is placed here
  // because the PI is the therapeutically defining agent (PI-priority policy,
  // same rationale as KR → Carfilzomib-based).
  // VRD: alternate spelling of RVD found in some backend records.
  // VT/VMP/PVD: the backend pipeline uses verbose key names for these regimens
  // ("VT Bortezomib and Thalidomide", "VMP Bortezomib, Melphalan, Prednisone",
  // "PVD Bortezomib, Pomalidomide, Dexamethasone"). The backend abbreviates them
  // to their short forms; verbose keys are listed here as a fallback.
  "Bortezomib monotherapy": "Bortezomib-based",
  "Bortezomib and Dexamethasone (Vd)": "Bortezomib-based",
  "Bortezomib and Dexamethasone (Vd) and Panobinostat": "Bortezomib-based",
  "PVD": "Bortezomib-based",  // uppercase-D variant — both PVD and PVd appear in data
  "PVD Bortezomib, Pomalidomide, Dexamethasone": "Bortezomib-based",  // pipeline key fallback
  "VMP": "Bortezomib-based",
  "VMP Bortezomib, Melphalan, Prednisone": "Bortezomib-based",        // pipeline key fallback
  "VT": "Bortezomib-based",
  "VT Bortezomib and Thalidomide": "Bortezomib-based",                // pipeline key fallback
  "RVD": "Bortezomib-based",
  "VRD": "Bortezomib-based",

  // ── Carfilzomib-based (PI) ────────────────────────────────────────────────
  // Dara-Kd and Isa-Kd are excluded (see mAb groups above).
  // KR (Carfilzomib + Lenalidomide) overlaps IMiD-based → placed here (PI priority).
  "Carfilzomib monotherapy": "Carfilzomib-based",
  "Carfilzomib and Dexamethasone (Kd)": "Carfilzomib-based",
  "Carfilzomib and Lenalidomide (KR)": "Carfilzomib-based",

  // ── RVD triplet (Bortezomib + Lenalidomide + Dexamethasone) ──────────────
  // Moved to Bortezomib-based (see above). No separate entry needed here.

  // ── Lenalidomide-based (IMiD) ─────────────────────────────────────────────
  // Dara-Rd and Elo-Rd excluded (see mAb groups above).
  // RD and Rd are the same backbone at different dose intensities → same group.
  // "High-Dose" / "Low-Dose" are the verbose backend names; (RD) / (Rd) are the
  // parenthetical abbreviation variants — all four map to the same group.
  "Lenalidomide monotherapy": "Lenalidomide-based",
  "Lenalidomide and Dexamethasone (Rd)": "Lenalidomide-based",
  "Lenalidomide and Dexamethasone (RD)": "Lenalidomide-based",
  "Lenalidomide and Dexamethasone Low-Dose": "Lenalidomide-based",
  "Lenalidomide and Dexamethasone High-Dose": "Lenalidomide-based",

  // ── Pomalidomide-based (IMiD) ─────────────────────────────────────────────
  // Dara-Pd, Isa-Pd, Elo-Pd excluded (see mAb groups above).
  "Pomalidomide monotherapy": "Pomalidomide-based",
  "Pomalidomide and Dexamethasone (Pd)": "Pomalidomide-based",

  // ── Ixazomib-based (oral PI) ──────────────────────────────────────────────
  // Ixazomib is a 3rd-generation oral proteasome inhibitor — structurally and
  // mechanistically distinct from both Bortezomib and Carfilzomib; own group.
  "Ixazomib monotherapy": "Ixazomib-based",
  "Ixazomib and Dexamethasone": "Ixazomib-based",

  // ── CELMoD agents ─────────────────────────────────────────────────────────
  // Cereblon E3 Ligase Modulators — related to IMiDs but a distinct drug class.
  "Iberdomide": "CELMoD",
  "Mezigdomide": "CELMoD",

  // ── Other oral agents ─────────────────────────────────────────────────────
  "Thalidomide monotherapy": "Other IMiD",

  // ── Selinexor-based (XPO1 inhibitor) ─────────────────────────────────────
  "Selinexor and Dexamethasone (Sd)": "Selinexor-based",

  // ── Venetoclax-based (BCL-2 inhibitor, t(11;14) MM) ──────────────────────
  // Venetoclax targets BCL-2, which is overexpressed in t(11;14) multiple myeloma.
  // IMWG 2024 guidelines endorse venetoclax for this genetically defined subgroup.
  "Venetoclax monotherapy": "Venetoclax-based",

  // ── CAR-T cell therapy ────────────────────────────────────────────────────
  "Ciltacabtagene autoleucel monotherapy": "CAR-T",
  "Idecabtagene vicleucel monotherapy": "CAR-T",

  // ── Bispecific antibodies ─────────────────────────────────────────────────
  "Teclistamab monotherapy": "Bispecific antibody",
  "Talquetamab monotherapy": "Bispecific antibody",
  "Elranatamab monotherapy": "Bispecific antibody",
  // Note: Linvoseltamab is stored without "monotherapy" suffix in OMOP (episode.py).
  "Linvoseltamab": "Bispecific antibody",

  // ── Antibody-drug conjugate (ADC) ────────────────────────────────────────
  "Belantamab mafodotin monotherapy": "ADC (BCMA)",

  // ── Transplant & conditioning ─────────────────────────────────────────────
  // Melphalan monotherapy = high-dose melphalan (HDM) conditioning before ASCT.
  // Grouping it with Transplant ensures it merges with Autogenous transplantation
  // into a single "Transplant (TLn)" node rather than appearing as salvage chemo.
  "Autogenous transplantation": "Transplant",
  "Harvesting of peripheral blood stem cells": "Transplant",
  "Melphalan monotherapy": "Transplant",
  "Busulfan and Melphalan": "Transplant conditioning",

  // ── Salvage chemotherapy ──────────────────────────────────────────────────
  // DCEP Dexamethasone, Cyclophosphamide, Etoposide, Cisplatin is the verbose
  // internal pipeline key for the OMOP concept "DCEP". The backend abbreviates
  // it to "DCEP"; the full key is listed here as a fallback.
  "DCEP": "Salvage chemotherapy",
  "DCEP Dexamethasone, Cyclophosphamide, Etoposide, Cisplatin": "Salvage chemotherapy",  // pipeline key fallback
  "DTPACE": "Salvage chemotherapy",
  "Bendamustine monotherapy": "Salvage chemotherapy",
  // MP (Melphalan + Prednisone): classic alkylator regimen for transplant-ineligible
  // elderly MM patients; grouped with salvage chemotherapy as it is no longer a
  // standard first-line option per EHA/ESMO 2024 guidelines.
  "Melphalan and Prednisone (MP)": "Salvage chemotherapy",

  // ── Supportive / non-cytotoxic ────────────────────────────────────────────
  "Best supportive care": "Supportive care",
  "Observation": "Supportive care",
  "Dexamethasone monotherapy": "Supportive care",
  "Radiation therapy": "Radiation",
  "Rituximab monotherapy": "Rituximab",

  // ── Abbreviation aliases ──────────────────────────────────────────────────
  // The backend combines multiple episodes within the same treatment line into a
  // single node using abbreviated names joined by " + " (e.g. "Dara-RVd + Mel").
  // These aliases ensure abbreviated component names resolve to the correct group
  // when `applyTreatmentGroupMapping` splits a combined name on " + ".
  // Source: backend `_TREATMENT_ABBREVIATIONS` dict in helper.py.

  // Daratumumab abbreviation
  "Dara": "Dara-based",

  // Elotuzumab abbreviation
  "Elo": "Elo-based",  // Elotuzumab monotherapy

  // Isatuximab abbreviation
  "Isa": "Isa-based",  // Isatuximab monotherapy

  // PI abbreviations
  "V":   "Bortezomib-based",   // Bortezomib monotherapy
  "Vd":  "Bortezomib-based",   // Bortezomib and Dexamethasone
  "PVd": "Bortezomib-based",   // Bortezomib and Dexamethasone and Panobinostat
  "K":   "Carfilzomib-based",  // Carfilzomib monotherapy
  "Kd":  "Carfilzomib-based",  // Carfilzomib and Dexamethasone
  "KR":  "Carfilzomib-based",  // Carfilzomib and Lenalidomide

  // IMiD abbreviations
  "Len":  "Lenalidomide-based",  // Lenalidomide monotherapy
  "RD":   "Lenalidomide-based",  // Lenalidomide + Dexamethasone high-dose
  "Rd":   "Lenalidomide-based",  // Lenalidomide + Dexamethasone low-dose
  "Pom":  "Pomalidomide-based",  // Pomalidomide monotherapy
  "Pd":   "Pomalidomide-based",  // Pomalidomide and Dexamethasone
  "Thal": "Other IMiD",          // Thalidomide monotherapy
  // Ixazomib abbreviations
  "Ixa":    "Ixazomib-based",     // Ixazomib monotherapy
  "IxaDex": "Ixazomib-based",     // Ixazomib and Dexamethasone

  // Selinexor
  "Sd": "Selinexor-based",  // Selinexor and Dexamethasone

  // Venetoclax
  "Ven": "Venetoclax-based",  // Venetoclax monotherapy

  // Novel / targeted
  "Bela":     "ADC (BCMA)",           // Belantamab mafodotin monotherapy
  "Cilta-cel": "CAR-T",               // Ciltacabtagene autoleucel
  "Ide-cel":  "CAR-T",               // Idecabtagene vicleucel

  // Salvage chemotherapy abbreviations
  "Mel":     "Transplant",            // Melphalan monotherapy (HDM conditioning before ASCT)
  "Harvest": "Transplant",            // Harvesting of peripheral blood stem cells
  "ASCT":    "Transplant",            // Autogenous transplantation
  "BuMel":   "Transplant conditioning", // Busulfan and Melphalan
  "Benda":   "Salvage chemotherapy",  // Bendamustine monotherapy
  "MP":      "Salvage chemotherapy",  // Melphalan and Prednisone

  // Bispecific antibody abbreviations
  "Talq": "Bispecific antibody",  // Talquetamab monotherapy
  "Tec":  "Bispecific antibody",  // Teclistamab monotherapy

  // Supportive abbreviations
  "BSC": "Supportive care",  // Best supportive care
  "Dex": "Supportive care",  // Dexamethasone monotherapy
  "RT":  "Radiation",        // Radiation therapy
  "R":   "Rituximab",        // Rituximab Mono
};
